<template>
    <div class="advanced-filter-chain-dropdown">
        <!-- Call to action div with selected label -->
        <div
            :class="props.type"
            class="advanced-filter-chain-dropdown__trigger p-sm display-flex align-items-center justify-between"
            @click="toggleDropdown"
        >
            <font-awesome-icon
                class="advanced-filter-chain-dropdown__check-icon mr-xs"
                :class="{ 'text-moat-600': selectedOption }"
                :icon="selectedOption ? iconPicker(selectedOption.type) : 'fas fa-circle-question'"
            />
            <p class="advanced-filter-chain-dropdown__selected text-truncate">
                {{ selectedOption ? selectedOption.label : placeholder }}
            </p>
        </div>

        <transition name="dropdown-fade">
            <div
                v-if="isOpen"
                class="advanced-filter-chain-dropdown__options elevation-5"
                v-click-outside="
                    () => {
                        if (isOpen) {
                            isOpen = false
                        }
                    }
                "
            >
                <!-- Search input -->
                <div class="advanced-filter-chain-dropdown__search-container px-sm pb-sm">
                    <input
                        ref="searchInput"
                        v-model="searchText"
                        type="text"
                        placeholder="Search options..."
                        class="input advanced-filter-chain-dropdown__search-input border-radius-md"
                        @click.stop
                        @keydown.enter.prevent="selectFirstOption"
                        @keydown.escape="closeDropdown"
                    />
                </div>

                <!-- Options list -->
                <div
                    v-for="(option, index) in filteredOptions"
                    :key="index"
                    class="advanced-filter-chain-dropdown__option px-xxxs display-flex align-items-center"
                    @click="selectOption(option)"
                >
                    <div
                        class="advanced-filter-chain-dropdown__option-inner display-flex align-items-center px-sm py-xs w-100"
                        :class="{
                            'advanced-filter-chain-dropdown__option-inner--selected':
                                isOptionSelected(option),
                        }"
                    >
                        <div
                            v-if="props.type !== 'operator'"
                            class="advanced-filter-chain-dropdown__check display-flex align-items-center mr-xxs"
                        >
                            <font-awesome-icon
                                class="advanced-filter-chain-dropdown__check-icon text-stone-700"
                                :icon="iconPicker(option.type)"
                            />
                        </div>
                        <p class="text-stone-700">{{ option.label }}</p>
                    </div>
                </div>

                <!-- No results message -->
                <div
                    v-if="filteredOptions.length === 0 && searchText.length > 0"
                    class="advanced-filter-chain-dropdown__no-results px-sm py-md text-center text-stone-500"
                >
                    No options found
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, watch, computed, defineEmits, nextTick } from 'vue'

const props = defineProps({
    options: {
        type: Array,
        default: () => [],
    },
    modelValue: {
        type: [String, Number, Object],
        default: null,
    },
    placeholder: {
        type: String,
        default: 'Select an option',
    },
    type: {
        type: String,
    },
})

const emit = defineEmits(['update:modelValue', 'change'])

const isOpen = ref(false)
const selectedOption = ref(null)
const searchText = ref('')
const searchInput = ref(null)

// Computed property to filter options based on search text
const filteredOptions = computed(() => {
    if (!searchText.value.trim()) {
        return props.options
    }

    return props.options.filter((option) =>
        option.label.toLowerCase().includes(searchText.value.toLowerCase())
    )
})

// Find the initially selected option based on modelValue
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue) {
            selectedOption.value = props.options.find(
                (option) => option.value === newValue || option === newValue
            )
        } else {
            selectedOption.value = null
        }
    },
    { immediate: true }
)

// Watch for dropdown open/close to handle search input focus and reset
watch(isOpen, async (newValue) => {
    if (newValue) {
        // Reset search text when opening
        searchText.value = ''
        // Focus the search input after the dropdown has rendered
        await nextTick()
        if (searchInput.value) {
            searchInput.value.focus()
        }
    }
})

const iconPicker = (type) => {
    switch (type) {
        case 'text':
            return 'fas fa-text'
        case 'number':
            return 'fas fa-hashtag'
        case 'date':
            return 'fas fa-calendar'
        case 'set':
            return 'fas fa-list-tree'
        default:
            return 'fas fa-check'
    }
}

// Toggle dropdown visibility
const toggleDropdown = () => {
    isOpen.value = !isOpen.value
}

// Close dropdown
const closeDropdown = () => {
    isOpen.value = false
}

// Select first option when Enter is pressed
const selectFirstOption = () => {
    if (filteredOptions.value.length > 0) {
        selectOption(filteredOptions.value[0])
    }
}

const isOptionSelected = (option) => {
    if (!selectedOption.value) return false

    return option.value !== undefined
        ? option.value === selectedOption.value.value
        : option === selectedOption.value
}

// Handle option selection
const selectOption = (option) => {
    selectedOption.value = option
    isOpen.value = false

    // Emit the selected value (support for v-model)
    emit('update:modelValue', option.value !== undefined ? option.value : option)

    // Also emit a change event with the full option object
    emit('change', option)
}
</script>

<style scoped lang="scss">
@forward '@/styles/new_styles.scss';

.advanced-filter-chain-dropdown {
    position: relative;
    width: 100%;
    max-width: 250px;

    &__trigger {
        height: 40px;
        border: 1px solid var(--color-stone-400);
        background: var(--color-stone-0);
        cursor: pointer;
        box-shadow:
            0px 1px 1px -0.5px var(--color-stone-200),
            0px 3px 3px -1.5px var(--color-stone-200);

        transition: all 0.3s ease-in-out;

        p,
        svg {
            color: var(--color-stone-700);
        }

        &:hover {
            background: var(--color-stone-200);
        }

        &.column {
            border-radius: var(--border-radius-lg) 0 0 var(--border-radius-lg);
            border-right: none;
        }

        &.operator {
            border-radius: 0;
        }
    }

    &__options {
        position: absolute;
        top: calc(100% + 10px);
        left: 0;
        width: 250px;
        max-height: 350px;
        overflow-y: auto;
        background-color: var(--color-stone-0);
        border-radius: var(--border-radius-lg);
        border: 1px solid var(--color-stone-300);
        z-index: 12;
        box-shadow: var(--shadow-md);
        padding-top: var(--spacing-sm);
    }

    &__search-container {
        background-color: var(--color-stone-0);
        z-index: 1;
    }

    &__search-input {
        width: 100%;
        padding: var(--spacing-sm);
        border: 1px solid var(--color-stone-300);
        border-radius: var(--border-radius-sm);
        background-color: var(--color-stone-0);
        font-size: 14px;
        color: var(--color-stone-700);
        outline: none;
        transition: border-color 0.2s ease;

        &:focus {
            border-color: var(--color-moat-600);
        }

        &::placeholder {
            color: var(--color-stone-500);
        }
    }

    &__option {
        padding-bottom: var(--spacing-sm);
    }

    &__option-inner {
        cursor: pointer;
        border-radius: var(--border-radius-sm);
        transition: all 0.3s ease-in-out;

        &:hover {
            background-color: var(--color-stone-200);
        }

        &--selected {
            background-color: var(--color-stone-200);
        }
    }

    &__check {
        min-width: 25px;
    }

    &__no-results {
        font-style: italic;
    }

    .dropdown-fade-enter-active,
    .dropdown-fade-leave-active {
        transition:
            opacity 0.3s,
            transform 0.3s;
    }

    .dropdown-fade-enter-from,
    .dropdown-fade-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }
}
</style>
